import type { ApiService, Logger } from "@packages/common";
import { API, LOGGER } from "@packages/common";
import type { Config } from "@packages/config";
import { CONFIG } from "@packages/config";
import { Inject, Injectable } from "@webmotors/ioc";
import type { SearchApiRequestDTO } from "./dtos/api/search-api-request.dto";
import type { SearchApiResponseDTO } from "./dtos/api/search-api-response.dto";
import type { FetchItemsParamsDTO } from "./dtos/fetch-items-params.dto";
import type { FetchItemsReturnDTO } from "./dtos/fetch-items-return.dto";
import { SearchTransformer } from "./transfomers/search.transformer";

@Injectable("SearchService")
export class SearchService {
  private readonly logger: Logger;

  constructor(
    @Inject(API)
    private readonly api: ApiService,
    @Inject(LOGGER)
    private readonly loggerFactory: Logger,
    @Inject(CONFIG)
    private readonly config: Config
  ) {
    this.logger = this.loggerFactory.create("SearchService");
  }

  /**
   * Send a fetch request to the service
   *
   * @param params
   * @returns
   */
  async fetchItems(params: FetchItemsParamsDTO): Promise<FetchItemsReturnDTO> {
    try {
      const url = this.getUrl(params.url);

      const requestParams = this.buildRequestParams(params, url);

      const data = await this.api.get<
        SearchApiRequestDTO,
        SearchApiResponseDTO
      >(`/search/${params.vehicleType}`, requestParams);

      return SearchTransformer.transform({
        params: data,
        vehicleType: params.vehicleType,
        actualPage: params.actualPage,
        displayPerPage: params.displayPerPage,
        photoPath: this.config.photoPath,
        baseURL: this.config.baseURL,
      });
    } catch (e) {
      this.logger.error(e);

      return {
        data: [],
        filters: [],
        filtersGrouped: [],
        dealerInfo: null,
        count: 0,
        actualPage: 0,
        nextPage: 0,
        hasNextPage: false,
      };
    }
  }

  private buildRequestParams(
    params: FetchItemsParamsDTO,
    url: string
  ): SearchApiRequestDTO {
    const requestParams: Record<string, unknown> = { url };

    const optionalParams: Array<keyof FetchItemsParamsDTO> = [
      "displayPerPage",
      "actualPage",
      "showMenu",
      "showMenuGrouped",
      "showCount",
      "showBreadCrumb",
      "order",
      "mediaZeroKm",
      "channel",
      "showRegionalized",
      "showResult",
      "showNewFilter",
    ];

    for (const key of optionalParams) {
      if (params[key] !== undefined) {
        requestParams[key] = params[key];
      }
    }

    return requestParams as SearchApiRequestDTO;
  }

  private getUrl(url: string) {
    const searchUrl = new URL(url.includes("?") ? url : `${url}?`);

    const segments = searchUrl.pathname.split("/").filter(Boolean);
    const firstTwoSegments = segments.slice(0, 2).join("/");
    searchUrl.pathname = `/${firstTwoSegments}`;

    return searchUrl.toString();
  }
}
